import { ASIN_FILTER_MAX } from "@adpilot/shared";
import {
  IconAlertCircle,
  IconClipboard,
  IconFold,
  IconInfoCircle,
  IconPlus,
  IconSearch,
  IconSelector,
  IconX,
} from "@tabler/icons-react";
import { useQueries } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { reportingFilterTriggerClassName } from "@/components/patterns/reporting-period-select";
import { Button } from "@/components/ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { useShopScope } from "@/hooks/use-shop-scope";
import { fetchProductCenterProducts } from "@/lib/product-center-api";
import { cn } from "@/lib/utils";
import {
  type AsinSelectionSummary,
  buildAsinSearchResults,
  listingsFromProductCenter,
  MAX_ASIN_TERMS,
  normalizePastedTerms,
  parseAsinTerms,
  selectableAsins,
  selectedAsins,
  summarizeAsinSelection,
  toggleAsin,
  toggleProduct,
} from "./asin-filter-model";
import { ASIN_NAV_ATTR, AsinProductRows, AsinResultsSkeleton } from "./asin-filter-rows";

export interface AsinFilterProps {
  /** Applied child ASINs. */
  value: string[];
  onValueChange: (asins: string[]) => void;
  disabled?: boolean;
}

const SEARCH_DEBOUNCE_MS = 300;
const NAV_SELECTOR = `[${ASIN_NAV_ATTR}]`;

/** Dashed "+ ASIN" add-filter trigger (Paper scenes' trigger); solid while its popup is open. */
const ADD_TRIGGER_CLASS =
  "group/trigger inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-sm border border-dashed border-control-border-hover pr-2.5 pl-2 text-sm font-medium text-text-secondary outline-none select-none transition-[background-color,border-color,color,box-shadow] duration-[var(--duration-fast)] hover:bg-control-hover hover:text-ink data-popup-open:border-solid data-popup-open:bg-control-open data-popup-open:text-ink focus-visible:border-solid focus-visible:border-focus-ring focus-visible:ring-2 focus-visible:ring-focus-halo disabled:cursor-not-allowed disabled:opacity-60";

/**
 * Dashboard ASIN filter (Paper "ASIN filter (search-only)"). There is no browsing
 * list: users type or paste ASINs / SKUs, matches come back selected and can be
 * deselected, and only Apply commits — closing discards the draft.
 */
export function AsinFilter({
  value,
  onValueChange,
  disabled = false,
}: AsinFilterProps): React.ReactElement {
  const { t } = useTranslation("dashboard");
  const [open, setOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const applied = value.length > 0;
  const appliedText = value.length === 1 ? value[0] : String(value.length);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <span ref={wrapperRef} className="relative inline-flex max-w-full shrink-0">
        <PopoverTrigger
          disabled={disabled}
          aria-label={
            applied
              ? t("overview.filters.asin.appliedLabel", { count: value.length })
              : t("overview.filters.asin.label")
          }
          title={applied ? value.join(", ") : undefined}
          className={applied ? cn(reportingFilterTriggerClassName, "pr-8") : ADD_TRIGGER_CLASS}
        >
          {applied ? (
            <>
              <span className="text-text-secondary">{t("overview.filters.asin.trigger")}</span>
              <span aria-hidden className="text-placeholder">
                ·
              </span>
              <span className="min-w-0 truncate tabular-nums">{appliedText}</span>
            </>
          ) : (
            <>
              <span aria-hidden className="flex size-3.5 shrink-0 items-center justify-center">
                <IconPlus className="size-3.5" stroke={1.75} />
              </span>
              {t("overview.filters.asin.trigger")}
            </>
          )}
        </PopoverTrigger>
        {applied && !disabled ? (
          <button
            type="button"
            aria-label={t("overview.filters.asin.clear")}
            onClick={() => {
              onValueChange([]);
              // The clear button unmounts with the applied state; keep focus on the trigger.
              requestAnimationFrame(() => wrapperRef.current?.querySelector("button")?.focus());
            }}
            className="absolute top-1/2 right-1.5 flex size-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-xs text-muted-foreground outline-none hover:bg-state-hover hover:text-ink focus-visible:ring-2 focus-visible:ring-focus-ring"
          >
            <IconX className="size-[13px]" stroke={2} aria-hidden />
          </button>
        ) : null}
      </span>
      <PopoverPopup
        align="start"
        sideOffset={4}
        initialFocus={searchRef}
        className="w-[400px] max-w-[calc(100vw-2rem)] overflow-hidden [&>[data-slot=popover-viewport]]:p-0"
      >
        {/* The popup mounts per open, so every open starts from the applied value. */}
        <AsinFilterPanel
          value={value}
          searchRef={searchRef}
          onApply={(asins) => {
            onValueChange(asins);
            setOpen(false);
          }}
        />
      </PopoverPopup>
    </Popover>
  );
}

function AsinFilterPanel({
  value,
  searchRef,
  onApply,
}: {
  value: string[];
  searchRef: React.RefObject<HTMLInputElement>;
  onApply: (asins: string[]) => void;
}): React.ReactElement {
  const { t } = useTranslation("dashboard");
  const { scopeShopIds, workspaceShops, isAll } = useShopScope();
  const [text, setText] = useState(() => value.join(", "));
  const [query, setQuery] = useState(text);
  // Matches come back selected; the draft is what the user deselected.
  const [excluded, setExcluded] = useState<ReadonlySet<string>>(() => new Set());
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const panelRef = useRef<HTMLDivElement>(null);
  const statusId = useId();

  useEffect(() => {
    if (text === query) return;
    const timer = window.setTimeout(() => setQuery(text), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [text, query]);

  const { terms, truncated } = useMemo(() => parseAsinTerms(query), [query]);
  // Search every workspace shop so matches outside the current scope can be shown
  // (disabled) instead of silently reading as "No match".
  const universe = useMemo(
    () => workspaceShops.map((shop) => shop.shopId).sort(),
    [workspaceShops],
  );
  const today = new Date().toISOString().slice(0, 10);
  const queries = useQueries({
    queries: terms.map((term) => ({
      queryKey: ["product-center", "asin-filter", universe, term.toUpperCase()],
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const listings = [];
        let page = 1;
        while (true) {
          const response = await fetchProductCenterProducts(
            {
              shopIds: universe,
              // Listings are not date-bound; the endpoint just requires a window.
              startDate: today,
              endDate: today,
              dimension: "SKU",
              page,
              pageSize: 100,
              search: term,
              active: "all",
              sortBy: "identity",
              sortDirection: "asc",
            },
            signal,
          );
          listings.push(...listingsFromProductCenter(response.data));
          if (!response.pagination.hasMore) return listings;
          page += 1;
        }
      },
      staleTime: 5 * 60_000,
      enabled: universe.length > 0,
    })),
  });

  const pending = queries.some((entry) => entry.isPending && entry.fetchStatus !== "idle");
  const failedTerms = terms.filter((_, index) => queries[index]?.isError);
  const settled = terms.flatMap((term, index) => {
    const data = queries[index]?.data;
    return data ? [{ term, listings: data }] : [];
  });
  const scope = isAll ? null : new Set(scopeShopIds);
  const results = buildAsinSearchResults(settled, scope);
  const { products } = results;
  const selectable = selectableAsins(products);
  const selected = selectedAsins(products, excluded);
  const summary = summarizeAsinSelection(products, excluded);
  const groups = products.filter((product) => product.parentAsin != null);
  const allExpanded = groups.length > 0 && groups.every((product) => expanded.has(product.key));
  const typing = text !== query;
  const showSkeleton = pending && products.length === 0;
  // The dashboard reads accept at most ASIN_FILTER_MAX child ASINs; a parent counts as all
  // of its variations, so a few families can already pass the cap.
  const overLimit = selected.length > ASIN_FILTER_MAX;
  // A failed term could hide an applied ASIN; applying then would silently drop it.
  const canApply =
    !pending &&
    !typing &&
    !overLimit &&
    failedTerms.length === 0 &&
    (selected.length > 0 || value.length > 0);
  const hasText = text.trim().length > 0;

  function apply() {
    if (canApply) onApply(selected);
  }

  function searchNow(next: string) {
    setText(next);
    setQuery(next);
  }

  function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData("text");
    if (!pasted) return;
    // A single-line input would drop pasted new lines and fuse the IDs together.
    event.preventDefault();
    const input = event.currentTarget;
    const start = input.selectionStart ?? text.length;
    const end = input.selectionEnd ?? text.length;
    const before = text.slice(0, start).replace(/[\s,]+$/, "");
    const after = text.slice(end).replace(/^[\s,]+/, "");
    const next = [before, normalizePastedTerms(pasted), after].filter(Boolean).join(", ");
    searchNow(next);
  }

  function toggleExpanded(key: string, force?: boolean) {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (force ?? !next.has(key)) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      apply();
      return;
    }
    const panel = panelRef.current;
    const searchInput = searchRef.current;
    if (!panel) return;
    const active = document.activeElement as HTMLElement | null;
    const inSearch = active === searchInput;
    if (inSearch && event.key === "Enter") {
      event.preventDefault();
      setQuery(text);
      return;
    }
    const items = [...panel.querySelectorAll<HTMLElement>(NAV_SELECTOR)].filter(
      (el) => !el.hasAttribute("disabled") && el.getAttribute("aria-disabled") !== "true",
    );
    const index = active ? items.indexOf(active) : -1;

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (index === -1 && !inSearch) return;
      event.preventDefault();
      const down = event.key === "ArrowDown";
      const nextIndex = down ? Math.min(index + 1, items.length - 1) : index - 1;
      if (nextIndex < 0) searchInput?.focus();
      else items[nextIndex]?.focus();
      return;
    }
    if ((event.key === "ArrowRight" || event.key === "ArrowLeft") && active && !inSearch) {
      const groupKey = active.dataset.asinGroup;
      if (!groupKey) return;
      event.preventDefault();
      toggleExpanded(groupKey, event.key === "ArrowRight");
      return;
    }
    // Typing anywhere in the list goes to search.
    if (
      !inSearch &&
      event.key.length === 1 &&
      event.key !== " " &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      searchInput?.focus();
    }
  }

  const footerText = showSkeleton
    ? t("overview.filters.asin.searchingShort")
    : selectionText(summary, t);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: delegates arrow/typing/⌘↵ keys from the focusable rows and search inside it.
    <div ref={panelRef} onKeyDown={handleKeyDown} className="flex flex-col">
      <div className="px-2.5 pt-2.5 pb-2">
        <div className="flex h-9 items-center gap-2 rounded-sm border border-control-border bg-control-default pr-2 pl-2.5 transition-[border-color,box-shadow] duration-[var(--duration-fast)] hover:border-control-border-hover focus-within:border-focus-ring focus-within:ring-[3px] focus-within:ring-focus-halo">
          <IconSearch
            className={cn("size-[15px] shrink-0", hasText ? "text-ink" : "text-muted-foreground")}
            stroke={1.75}
            aria-hidden
          />
          <input
            ref={searchRef}
            type="text"
            value={text}
            onChange={(event) => setText(event.target.value)}
            onPaste={handlePaste}
            placeholder={t("overview.filters.asin.searchPlaceholder")}
            aria-label={t("overview.filters.asin.searchPlaceholder")}
            aria-describedby={terms.length > 0 ? statusId : undefined}
            autoComplete="off"
            spellCheck={false}
            className="h-full min-w-0 flex-1 bg-transparent text-sm text-ink tabular-nums outline-none placeholder:text-placeholder"
          />
          {pending ? (
            <span className="flex size-5 shrink-0 items-center justify-center">
              <Spinner
                className="size-4 text-muted-foreground"
                aria-label={t("overview.filters.asin.searchingShort")}
              />
            </span>
          ) : hasText ? (
            <button
              type="button"
              aria-label={t("overview.filters.asin.clearSearch")}
              onClick={() => {
                searchNow("");
                searchRef.current?.focus();
              }}
              className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-xs text-muted-foreground outline-none hover:bg-state-hover hover:text-ink focus-visible:ring-2 focus-visible:ring-focus-ring"
            >
              <IconX className="size-[13px]" stroke={2} aria-hidden />
            </button>
          ) : null}
        </div>
      </div>

      {terms.length === 0 ? (
        <div className="flex flex-col items-center gap-1.5 px-6 pt-5 pb-6 text-center">
          <IconClipboard className="size-5 text-placeholder" stroke={1.75} aria-hidden />
          <p className="text-sm font-medium text-ink">{t("overview.filters.asin.emptyTitle")}</p>
          <p className="text-xs text-muted-foreground">
            {t("overview.filters.asin.emptyDescription")}
          </p>
        </div>
      ) : (
        <>
          <div className="flex min-h-[18px] items-center justify-between gap-3 px-3.5 pb-1">
            <p
              id={statusId}
              aria-live="polite"
              className="min-w-0 truncate text-badge text-muted-foreground tabular-nums"
            >
              {showSkeleton
                ? t("overview.filters.asin.searching", {
                    terms: t("overview.filters.asin.termCount", { count: terms.length }),
                    shops: t("overview.filters.asin.shopCount", { count: universe.length }),
                  })
                : t("overview.filters.asin.foundOfTotal", {
                    found: results.matchedTerms.length,
                    count: terms.length,
                  })}
            </p>
            {!showSkeleton && (groups.length > 0 || selectable.length > 0) ? (
              <div className="flex shrink-0 items-center gap-2">
                {groups.length > 0 ? (
                  <MetaAction
                    icon={allExpanded ? IconFold : IconSelector}
                    label={t(
                      allExpanded
                        ? "overview.filters.asin.collapseAll"
                        : "overview.filters.asin.expandAll",
                    )}
                    onClick={() =>
                      setExpanded(
                        allExpanded ? new Set() : new Set(groups.map((product) => product.key)),
                      )
                    }
                  />
                ) : null}
                {groups.length > 0 && selectable.length > 0 ? (
                  <span aria-hidden className="h-2.5 w-px shrink-0 bg-line" />
                ) : null}
                {selectable.length > 0 ? (
                  <MetaAction
                    label={t(
                      selected.length > 0
                        ? "overview.filters.asin.deselectAll"
                        : "overview.filters.asin.selectAll",
                    )}
                    onClick={() =>
                      setExcluded(selected.length > 0 ? new Set(selectable) : new Set())
                    }
                  />
                ) : null}
              </div>
            ) : null}
          </div>
          {truncated ? (
            <p className="px-3.5 pb-1 text-badge text-warning-foreground">
              {t("overview.filters.asin.tooManyTerms", { count: MAX_ASIN_TERMS })}
            </p>
          ) : null}

          {showSkeleton ? (
            <AsinResultsSkeleton />
          ) : products.length > 0 ? (
            <div className="paper-scrollbar flex max-h-[360px] flex-col overflow-y-auto px-1 pb-2">
              {products.map((product) => (
                <AsinProductRows
                  key={product.key}
                  product={product}
                  excluded={excluded}
                  expanded={expanded.has(product.key)}
                  t={t}
                  onToggleExpanded={() => toggleExpanded(product.key)}
                  onToggleProduct={() => setExcluded(toggleProduct(product, excluded))}
                  onToggleAsin={(asin) => setExcluded(toggleAsin(asin, excluded))}
                />
              ))}
            </div>
          ) : null}

          {!showSkeleton && (results.unmatchedTerms.length > 0 || failedTerms.length > 0) ? (
            <div className="flex flex-col gap-1 px-2.5 pb-2.5">
              {results.unmatchedTerms.length > 0 ? (
                <p className="flex items-start gap-2 rounded-sm bg-table-footer px-2.5 py-2">
                  <IconInfoCircle
                    className="mt-px size-3.5 shrink-0 text-muted-foreground"
                    stroke={1.75}
                    aria-hidden
                  />
                  <span className="min-w-0 text-xs text-text-secondary">
                    {t("overview.filters.asin.noMatchFor")}{" "}
                    <span className="text-badge break-all text-ink">
                      {results.unmatchedTerms.join(", ")}
                    </span>
                  </span>
                </p>
              ) : null}
              {failedTerms.length > 0 ? (
                <div
                  role="alert"
                  className="flex items-center gap-2 rounded-sm bg-table-footer py-1 pr-1 pl-2.5"
                >
                  <IconAlertCircle
                    className="size-3.5 shrink-0 text-destructive-foreground"
                    stroke={1.75}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1 truncate text-xs text-text-secondary">
                    {t("overview.filters.asin.searchFailed", { terms: failedTerms.join(", ") })}
                  </span>
                  <Button
                    variant="ghost"
                    size="xs"
                    className="rounded-sm px-2 text-text-secondary"
                    onClick={() => {
                      for (const entry of queries) if (entry.isError) void entry.refetch();
                    }}
                  >
                    {t("overview.filters.asin.retry")}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}

      <div className="flex shrink-0 items-center gap-2 border-t border-line px-3 py-2.5">
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-xs tabular-nums",
            overLimit
              ? "text-warning-foreground"
              : summary.kind === "none"
                ? "text-muted-foreground"
                : "text-text-secondary",
          )}
          aria-live="polite"
        >
          {overLimit
            ? t("overview.filters.asin.overLimit", {
                count: ASIN_FILTER_MAX,
                selected: selected.length,
              })
            : footerText}
        </span>
        <Button size="xs" className="rounded-sm px-3" disabled={!canApply} onClick={apply}>
          {t("overview.filters.asin.apply")}
        </Button>
      </div>
    </div>
  );
}

function MetaAction({
  icon: Icon,
  label,
  onClick,
}: {
  icon?: typeof IconSelector;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex cursor-pointer items-center gap-[3px] rounded-xs text-badge font-medium text-text-secondary outline-none hover:text-ink focus-visible:ring-2 focus-visible:ring-focus-ring"
    >
      {Icon ? <Icon className="size-3 shrink-0" stroke={2} aria-hidden /> : null}
      {label}
    </button>
  );
}

function selectionText(
  summary: AsinSelectionSummary,
  t: ReturnType<typeof useTranslation<"dashboard">>["t"],
): string {
  switch (summary.kind) {
    case "none":
      return t("overview.filters.asin.nothingSelected");
    case "products":
      return t("overview.filters.asin.productsSelected", { count: summary.products });
    case "withVariations":
      return `${t("overview.filters.asin.productsSelected", { count: summary.products })} · ${t(
        "overview.filters.asin.includesVariations",
        { count: summary.variations },
      )}`;
    case "partialVariations":
      return `${t("overview.filters.asin.productCount", { count: summary.products })} · ${t(
        "overview.filters.asin.variationsOfTotal",
        { selected: summary.selected, count: summary.total },
      )}`;
  }
}
